/**
 * Field Value Resolver
 * Utility to resolve UUIDs to human-readable names for table display
 */

import { LinkedDataSourceType } from "@/lib/dataSources/types";
import { getCachedTypes } from "@/lib/stores/typesStore";
import { getCachedLibraryItems } from "@/lib/stores/objectsStore";
import { getCachedDialogs } from "@/lib/stores/dialogStore";
import { getCachedTemplates } from "@/lib/stores/templatesStore";
import { getCachedTags } from "@/lib/stores/tagsStore";
import { getGameMembers } from "@/lib/stores/unifiedUserStore";
import { getFolders, Folder } from "@/lib/stores/folderStore";

/**
 * Resolve UUID(s) to display name(s) using cached store data
 * @param value - Single UUID, comma-separated string, or array of UUIDs
 * @param linkedDataSource - Type of linked data (types, objects, etc.)
 * @param gameId - Current game ID for filtering
 * @returns Resolved display name(s) as a string
 */
export function resolveFieldDisplayValue(
  value: string | string[] | null | undefined,
  linkedDataSource: LinkedDataSourceType | undefined,
  gameId?: string
): string {
  // Handle null/undefined
  if (!value || !linkedDataSource) {
    return String(value || "");
  }

  // Normalize value to array
  let valueArray: string[];
  if (Array.isArray(value)) {
    valueArray = value;
  } else if (typeof value === "string") {
    // Split comma-separated values
    valueArray = value.includes(",") ? value.split(",").map(v => v.trim()) : [value];
  } else {
    return String(value);
  }

  // Filter empty values
  valueArray = valueArray.filter(v => v && v.trim());
  
  if (valueArray.length === 0) {
    return "";
  }

  // Resolve each value
  const resolvedNames = valueArray.map(uuid => 
    resolveSingleValue(uuid, linkedDataSource, gameId)
  );

  // Join multiple values with comma
  return resolvedNames.join(", ");
}

/**
 * Resolve a single UUID to its display name
 */
function resolveSingleValue(
  uuid: string,
  linkedDataSource: LinkedDataSourceType,
  gameId?: string
): string {
  if (!uuid || !uuid.trim()) return "";

  const trimmedUuid = uuid.trim();

  try {
    switch (linkedDataSource) {
      case "types": {
        if (!gameId) return trimmedUuid;
        const types = getCachedTypes(gameId);
        
        // Types are structured with elements inside
        // Need to search through all type elements
        for (const type of types) {
          if (type.data && Array.isArray(type.data)) {
            const element = type.data.find(el => el.id === trimmedUuid);
            if (element) {
              return element.name || trimmedUuid;
            }
          }
        }
        return trimmedUuid;
      }

      case "objects": {
        if (!gameId) return trimmedUuid;
        const objects = getCachedLibraryItems(gameId);
        const item = objects.find(obj => obj.id === trimmedUuid);
        return item?.name || trimmedUuid;
      }

      case "templates": {
        if (!gameId) return trimmedUuid;
        const templates = getCachedTemplates(gameId);
        const template = templates.find(t => t.id === trimmedUuid);
        return template?.name || trimmedUuid;
      }

      case "tags": {
        if (!gameId) return trimmedUuid;
        const tags = getCachedTags(gameId);
        const tag = tags.find(t => t.id === trimmedUuid);
        return tag?.name || trimmedUuid;
      }

      case "users": {
        if (!gameId) return trimmedUuid;
        const users = getGameMembers(gameId);
        const user = users.find(u => u.id === trimmedUuid);
        return user?.username || user?.full_name || user?.email || trimmedUuid;
      }

      case "folders": {
        if (!gameId) return trimmedUuid;
        // Folders need item type - default to 'library'
        // We'll search all folder types to find the match
        const folderTypes: ('library' | 'documents' | 'templates')[] = ['library', 'documents', 'templates'];
        
        for (const folderType of folderTypes) {
          const folders = getFolders(gameId, folderType);
          const folder = findFolderById(folders, trimmedUuid);
          if (folder) {
            return getFolderPath(folders, trimmedUuid);
          }
        }
        return trimmedUuid;
      }

      case "dialogs": {
        if (!gameId) return trimmedUuid;
        const dialogs = getCachedDialogs(gameId);
        const dialog = dialogs.find(d => d.id === trimmedUuid);
        return dialog?.name || trimmedUuid;
      }

      case "statuses": {
        // Statuses are simple strings, not UUIDs
        // Just return the value formatted nicely
        return trimmedUuid.charAt(0).toUpperCase() + trimmedUuid.slice(1).replace(/_/g, " ");
      }

      default:
        return trimmedUuid;
    }
  } catch (error) {
    console.warn(`Error resolving field value for ${linkedDataSource}:`, error);
    return trimmedUuid;
  }
}

/**
 * Helper to find a folder by ID in a nested folder structure
 */
function findFolderById(folders: Folder[], folderId: string): Folder | null {
  for (const folder of folders) {
    if (folder.id === folderId) {
      return folder;
    }
    
    // Search in subfolders
    if (folder.subfolder && folder.subfolder.length > 0) {
      const found = findFolderById(folder.subfolder, folderId);
      if (found) return found;
    }
    
    // Search in children (alternative property name)
    if (folder.children && folder.children.length > 0) {
      const found = findFolderById(folder.children, folderId);
      if (found) return found;
    }
  }
  
  return null;
}

/**
 * Helper to get the full path of a folder
 */
function getFolderPath(folders: Folder[], folderId: string, parentPath: string = ""): string {
  for (const folder of folders) {
    const currentPath = parentPath ? `${parentPath}/${folder.name}` : `/${folder.name}`;
    
    if (folder.id === folderId) {
      return currentPath;
    }
    
    // Search in subfolders
    if (folder.subfolder && folder.subfolder.length > 0) {
      const found = getFolderPath(folder.subfolder, folderId, currentPath);
      if (found) return found;
    }
    
    // Search in children
    if (folder.children && folder.children.length > 0) {
      const found = getFolderPath(folder.children, folderId, currentPath);
      if (found) return found;
    }
  }
  
  return folderId;
}

/**
 * Map field type string (from blueprint) to LinkedDataSourceType
 * Used when extracting metadata from blueprint fields
 */
export function mapFieldTypeToLinkedDataSource(
  fieldType: string
): LinkedDataSourceType | undefined {
  switch (fieldType) {
    case "types":
      return "types";
    case "glossaryItem":
    case "glossary-item":
      return "objects";
    case "dialog":
      return "dialogs";
    default:
      return undefined;
  }
}
